import React, { useEffect, useState, useCallback } from 'react';
import SelfApprovedChip from '../common/SelfApprovedChip';
import FlowStrip from '../../help/FlowStrip';
import StatusStagesChip from '../../help/StatusStagesChip';
import { purchaseFlow } from '../../help/flows';
import { PURCHASE_ORDER_STAGES } from '../../help/statusStages';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Alert, Box, Button, Chip, CircularProgress, Paper,
  Stack, Table, TableBody, TableCell, TableHead, TableRow,
  TextField, Typography,
} from '@mui/material';
import { PurchaseOrderService } from '../../services/purchaseOrder.service';
import { ItemService } from '../../services/item.service';
import { SupplierService } from '../../services/supplier.service';
import { loadSuppliersSafely } from '../../utils/supplierAccess';
import { AuthService } from '../../services/auth.service';
import {
  PurchaseOrder,
  PurchaseOrderCreateDTO,
  PurchaseOrderLine,
  PurchaseOrderStatus,
} from '../../interfaces/procurement/purchaseOrder.interface';
import { Item } from '../../interfaces/item.interface';
import { Supplier } from '../../interfaces/procurement/supplier.interface';
import { useToast } from '../../context/ToastContext';
import { usePermission } from '../../hooks/usePermission';
import { PERMISSIONS } from '../../constants/permissions';
import ScrollableTable from '../common/ScrollableTable';
import { ConfirmDialog } from '../common/ConfirmDialog';
import SearchableSelect from '../common/SearchableSelect';
import { formatMoney, humanizeEnum } from '../../utils/format';
import { formatDate } from '../../utils/dateUtils';
import AttachmentsPanel from '../common/AttachmentsPanel'; // W3-K
import SupplierAckLine from './supplierportal/SupplierAckLine'; // F8
import { formatFc, isForeignCurrency } from '../../utils/fcDocuments'; // G5a

type Action = 'edit' | 'save' | 'cancelEdit' | 'submit' | 'approve' | 'reject' | 'markOrdered' | 'cancel' | 'amend' | 'delete';

function actionsForStatus(status: PurchaseOrderStatus, editMode: boolean): Action[] {
  if (editMode) return ['save', 'cancelEdit'];
  switch (status) {
    case 'DRAFT': return ['edit', 'submit', 'cancel', 'delete'];
    case 'PENDING_APPROVAL': return ['cancel'];
    case 'APPROVED': return ['markOrdered', 'cancel'];
    case 'ORDERED': return ['amend', 'cancel'];
    case 'PARTIALLY_RECEIVED': return ['amend'];
    case 'RECEIVED': return ['amend'];
    case 'CLOSED': return [];
    default: return [];
  }
}

interface LineDraft {
  itemId: string;
  quantity: number;
  unitPrice: number;
}

function linesToDraft(lines: PurchaseOrderLine[]): LineDraft[] {
  // G5a: a foreign PO edits its prices in the PO currency
  return lines.map(l => ({ itemId: l.itemId, quantity: l.quantity, unitPrice: l.foreignUnitPrice ?? l.unitPrice }));
}

function linesChanged(original: LineDraft[], draft: LineDraft[]): boolean {
  if (original.length !== draft.length) return true;
  return original.some((o, i) =>
    o.itemId !== draft[i].itemId || o.quantity !== draft[i].quantity || o.unitPrice !== draft[i].unitPrice
  );
}

const STATUS_COLORS: Record<PurchaseOrderStatus, 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning'> = {
  DRAFT: 'default',
  PENDING_APPROVAL: 'warning',
  APPROVED: 'info',
  ORDERED: 'primary',
  PARTIALLY_RECEIVED: 'secondary',
  RECEIVED: 'success',
  CLOSED: 'default',
  REJECTED: 'error',
  CANCELLED: 'error',
};

export const PurchaseOrderDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const toast = useToast();
  const canManagePo = usePermission(PERMISSIONS.PURCHASE_ORDER_MANAGE);
  const canClosePo = usePermission(PERMISSIONS.PURCHASE_ORDER_CLOSE);

  const [po, setPo] = useState<PurchaseOrder | null>(null);
  const [items, setItems] = useState<Item[]>([]);
  const [suppliers, setSuppliers] = useState<Supplier[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [downloadingPdf, setDownloadingPdf] = useState(false);

  // Edit mode state
  const [editMode, setEditMode] = useState(false);
  const [draftLines, setDraftLines] = useState<LineDraft[]>([]);
  const [draftExpectedDate, setDraftExpectedDate] = useState('');
  const [draftNotes, setDraftNotes] = useState('');
  const [draftSupplierId, setDraftSupplierId] = useState('');
  const [draftOrderDate, setDraftOrderDate] = useState('');
  const [draftTaxAmount, setDraftTaxAmount] = useState(0);
  const [reapprovalBannerVisible, setReapprovalBannerVisible] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const loadPO = useCallback(async () => {
    if (!id) return;
    try {
      const result = await PurchaseOrderService.getById(id);
      setPo(result);
    } catch (e) {
      toast.showApiError(e, 'Failed to load purchase order');
    }
  }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const institutionId = AuthService.getSelectedInstitution()!!;
    Promise.all([
      loadPO(),
      ItemService.getItemsByInstitution(0, 500).then(r => setItems(r.content)),
      loadSuppliersSafely(() => SupplierService.listByInstitution(0, 500, 'ALL'))
        .then(r => {
          setSuppliers(r.suppliers);
          // Suppliers only feed the edit-mode picker: a read-only viewer lacking SUPPLIER_VIEW
          // should not get a permission toast on every PO they open.
          if (r.error && (canManagePo || !r.forbidden)) toast.showError(r.error);
        }),
    ]).finally(() => setLoading(false));
  }, [loadPO]); // eslint-disable-line react-hooks/exhaustive-deps

  const enterEditMode = () => {
    if (!po) return;
    setDraftLines(linesToDraft(po.lines));
    setDraftExpectedDate(po.expectedDate ?? '');
    setDraftNotes(po.notes ?? '');
    setDraftSupplierId(po.supplierId);
    setDraftOrderDate(po.orderDate);
    setDraftTaxAmount(po.taxAmount);
    setEditMode(true);
  };

  const cancelEdit = () => {
    setEditMode(false);
  };

  const fullDto = (): PurchaseOrderCreateDTO => ({
    supplierId: draftSupplierId,
    orderDate: draftOrderDate,
    expectedDate: draftExpectedDate || undefined,
    notes: draftNotes || undefined,
    taxAmount: draftTaxAmount,
    institutionId: AuthService.getSelectedInstitution()!!,
    lines: draftLines.map(l => ({ itemId: l.itemId, quantity: l.quantity, unitPrice: l.unitPrice })),
    // G5a: an edit keeps the currency, the stored rate and the LC link
    ...(po && isForeignCurrency(po.currencyCode)
      ? { currencyCode: po.currencyCode, exchangeRate: po.exchangeRate, taxAmount: 0 } : {}),
    ...(po?.backToBackLcId ? { backToBackLcId: po.backToBackLcId } : {}),
  });

  const handleSave = async () => {
    if (!po) return;
    try {
      setSaving(true);
      if (po.status === 'DRAFT') {
        await PurchaseOrderService.updateDraft(po.id, fullDto());
      } else {
        const originalLines = linesToDraft(po.lines);
        const onlyHeaderChanged =
          !linesChanged(originalLines, draftLines) &&
          draftSupplierId === po.supplierId &&
          draftOrderDate === po.orderDate &&
          draftTaxAmount === po.taxAmount;

        if (onlyHeaderChanged) {
          await PurchaseOrderService.applyMinorAmendment(po.id, {
            expectedDate: draftExpectedDate || undefined,
            notes: draftNotes || undefined,
          });
        } else {
          const updated = await PurchaseOrderService.applyMaterialAmendment(po.id, fullDto());
          if (updated.status === 'PENDING_APPROVAL') setReapprovalBannerVisible(true);
        }
      }
      await loadPO();
      setEditMode(false);
    } catch (e) {
      toast.showApiError(e, 'Failed to save changes');
    } finally {
      setSaving(false);
    }
  };

  const handleSubmit = async () => {
    if (!po) return;
    try {
      await PurchaseOrderService.submit(po.id);
      toast.showSuccess('Purchase order submitted for approval');
      await loadPO();
    } catch (e) {
      toast.showApiError(e, 'Failed to submit purchase order');
    }
  };

  const handleMarkOrdered = async () => {
    if (!po) return;
    try {
      await PurchaseOrderService.markOrdered(po.id);
      toast.showSuccess('Purchase order marked as ordered');
      await loadPO();
    } catch (e) {
      toast.showApiError(e, 'Failed to mark as ordered');
    }
  };

  const handleCancel = async () => {
    if (!po) return;
    try {
      await PurchaseOrderService.cancel(po.id);
      toast.showSuccess('Purchase order cancelled');
      await loadPO();
    } catch (e) {
      toast.showApiError(e, 'Failed to cancel purchase order');
    }
  };

  const handleDelete = async () => {
    if (!po) return;
    setDeleting(true);
    try {
      await PurchaseOrderService.delete(po.id);
      toast.showSuccess('Purchase order deleted');
      navigate('/procurement/purchase-orders');
    } catch (e) {
      toast.showApiError(e, 'Failed to delete purchase order');
      setDeleting(false);
      setDeleteDialogOpen(false);
    }
  };

  const handleDownloadPdf = async () => {
    if (!po) return;
    setDownloadingPdf(true);
    try {
      await PurchaseOrderService.downloadPdf(po.id, po.poNumber);
    } catch (e) {
      toast.showApiError(e, 'Failed to download PDF');
    } finally {
      setDownloadingPdf(false);
    }
  };

  const updateDraftLine = (idx: number, patch: Partial<LineDraft>) => {
    setDraftLines(ls => ls.map((l, i) => i === idx ? { ...l, ...patch } : l));
  };

  if (loading) return <Box sx={{ p: 3 }}><CircularProgress /></Box>;
  if (!po) return <Typography sx={{ p: 3 }}>Purchase order not found</Typography>;
  const foreign = isForeignCurrency(po.currencyCode); // G5a

  // Server guards: edit/amend/submit/markOrdered/delete = PURCHASE_ORDER_MANAGE, cancel = PURCHASE_ORDER_CLOSE.
  const actions = actionsForStatus(po.status, editMode).filter(a =>
    a === 'cancel' ? canClosePo : canManagePo);

  return (
    <Box sx={{ p: 3 }} data-help-page="procurement.poDetail.page">
      <FlowStrip data-help="procurement.po.flow" stages={purchaseFlow({ current: 'po', requisition: { id: po.requisitionId }, po: { no: po.poNumber } })} />
      {reapprovalBannerVisible && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Material amendment detected — PO sent back for re-approval.
        </Alert>
      )}

      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 1 }}>
        <Box>
          <Typography variant="h5">{po.poNumber}</Typography>
          <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
            <StatusStagesChip data-help="procurement.po.statusStages" label={humanizeEnum(po.status)} status={po.status} stages={PURCHASE_ORDER_STAGES} color={STATUS_COLORS[po.status]} />
            <SelfApprovedChip selfApproved={po.selfApproved} rule={po.selfApprovalRule} />
            <Typography variant="body2" color="text.secondary">Rev {po.revision}</Typography>
          </Box>
          {po.id && <SupplierAckLine purchaseOrderId={po.id} />} {/* F8 */}
        </Box>
        <Stack direction="row" spacing={1}>
          <Button data-help="procurement.poDetail.pdf" variant="outlined" onClick={handleDownloadPdf} disabled={downloadingPdf}>
            {downloadingPdf ? 'Preparing…' : 'Download as PDF'}
          </Button>
          {actions.includes('edit') && (
            <Button data-help="procurement.poDetail.edit" variant="outlined" onClick={enterEditMode}>Edit</Button>
          )}
          {actions.includes('amend') && (
            <Button data-help="procurement.poDetail.amend" variant="outlined" onClick={enterEditMode}>Amend</Button>
          )}
          {actions.includes('submit') && (
            <Button data-help="procurement.poDetail.submit" variant="contained" color="primary" onClick={handleSubmit}>Submit</Button>
          )}
          {actions.includes('markOrdered') && (
            <Button data-help="procurement.poDetail.markOrdered" variant="contained" color="primary" onClick={handleMarkOrdered}>Mark Ordered</Button>
          )}
          {actions.includes('cancel') && (
            <Button data-help="procurement.poDetail.cancel" variant="outlined" color="warning" onClick={handleCancel}>Cancel PO</Button>
          )}
          {actions.includes('delete') && (
            <Button data-help="procurement.poDetail.delete" variant="outlined" color="error" onClick={() => setDeleteDialogOpen(true)}>Delete</Button>
          )}
          {actions.includes('save') && (
            <Button data-help="procurement.poDetail.save" variant="contained" color="primary" onClick={handleSave} disabled={saving}>Save</Button>
          )}
          {actions.includes('cancelEdit') && (
            <Button data-help="procurement.poDetail.discard" variant="outlined" onClick={cancelEdit}>Discard</Button>
          )}
        </Stack>
      </Box>

      {/* Summary */}
      <Paper sx={{ p: 2, mb: 2, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }} data-help-section="procurement.poDetail.section.summary">
        {editMode ? (
          <>
            <SearchableSelect<Supplier>
              options={suppliers}
              value={draftSupplierId}
              data-help="procurement.poDetail.field.supplier"
              onChange={id => setDraftSupplierId(id)}
              getOptionLabel={s => s.name}
              label="Supplier"
              size="medium"
            />
            <TextField
              data-help="procurement.poDetail.field.orderDate"
              label="Order Date"
              type="date"
              value={draftOrderDate}
              onChange={e => setDraftOrderDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              data-help="procurement.poDetail.field.expectedDate"
              label="Expected Date"
              type="date"
              value={draftExpectedDate}
              onChange={e => setDraftExpectedDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              data-help="procurement.poDetail.field.tax"
              label="Tax Amount"
              type="number"
              value={foreign ? 0 : draftTaxAmount}
              disabled={foreign}
              onChange={e => setDraftTaxAmount(Number(e.target.value))}
            />
            <TextField
              sx={{ gridColumn: '1 / -1' }}
              data-help="procurement.poDetail.field.notes"
              label="Notes"
              multiline
              rows={2}
              value={draftNotes}
              onChange={e => setDraftNotes(e.target.value)}
            />
          </>
        ) : (
          <>
            <Typography variant="body2" title={po.supplierId}>
              <strong>Supplier:</strong> {po.supplierName ?? '—'}
            </Typography>
            <Typography variant="body2"><strong>Order Date:</strong> {formatDate(po.orderDate)}</Typography>
            <Typography variant="body2">
              <strong>Expected Date:</strong> {po.expectedDate ? formatDate(po.expectedDate) : '-'}
            </Typography>
            <Typography variant="body2"><strong>Tax Amount:</strong> {formatMoney(po.taxAmount)}</Typography>
            {foreign && (
              <Typography variant="body2">
                <strong>Currency:</strong> {po.currencyCode} at {po.exchangeRate} (indicative: each receipt books its own rate)
              </Typography>
            )}
            {po.backToBackLcId && (
              <Typography variant="body2">
                <strong>B2B LC:</strong> {po.backToBackLcNumber ?? po.backToBackLcId} (settled through the LC acceptance, not billed)
              </Typography>
            )}
            <Typography variant="body2" sx={{ gridColumn: '1 / -1' }}>
              <strong>Notes:</strong> {po.notes ?? '-'}
            </Typography>
          </>
        )}
      </Paper>

      {/* Lines */}
      <Paper sx={{ p: 2, mb: 2 }} data-help-section="procurement.poDetail.lines">
        <Typography variant="subtitle1" sx={{ mb: 1 }}>Lines</Typography>
        <ScrollableTable>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell data-help="procurement.poLine.col.item">Item</TableCell>
                  <TableCell data-help="procurement.poLine.col.qty">Quantity</TableCell>
                  <TableCell data-help="procurement.poLine.col.price">Unit Price{foreign ? ` (${po.currencyCode})` : ''}</TableCell>
                  <TableCell data-help="procurement.poLine.col.total">Line Total{foreign ? ` (${po.currencyCode})` : ''}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {editMode ? (
                  draftLines.map((l, idx) => (
                    <TableRow key={idx}>
                      <TableCell>
                        <SearchableSelect<Item>
                          options={items.filter(i => i.itemKind !== 'KIT')}   // F6c: a kit is never bought; order its components
                          value={l.itemId}
                          onChange={id => updateDraftLine(idx, { itemId: id })}
                          getOptionLabel={i => i.itemName}
                          label="Item"
                          data-help="procurement.poDetail.field.lineItem"
                        />
                      </TableCell>
                      <TableCell>
                        <TextField
                          type="number"
                          size="small"
                          label="Quantity"
                          data-help="procurement.poDetail.field.lineQty"
                          value={l.quantity}
                          onChange={e => updateDraftLine(idx, { quantity: Number(e.target.value) })}
                          inputProps={{ 'aria-label': 'Quantity' }}
                        />
                      </TableCell>
                      <TableCell>
                        <TextField
                          type="number"
                          size="small"
                          label={foreign ? `Unit Price (${po.currencyCode})` : 'Unit Price'}
                          data-help="procurement.poDetail.field.linePrice"
                          value={l.unitPrice}
                          onChange={e => updateDraftLine(idx, { unitPrice: Number(e.target.value) })}
                        />
                      </TableCell>
                      <TableCell>{foreign && po.currencyCode ? formatFc(po.currencyCode, l.quantity * l.unitPrice) : formatMoney(l.quantity * l.unitPrice)}</TableCell>
                    </TableRow>
                  ))
                ) : (
                  (po.lines ?? []).map(l => (
                    <TableRow key={l.id}>
                      <TableCell>{l.itemName ?? items.find(i => i.id === l.itemId)?.itemName ?? '—'}{l.itemCode ? ` (${l.itemCode})` : ''}</TableCell>
                      <TableCell>{l.quantity}{l.unit ? ` ${l.unit}` : ''}{l.receivedQty ? ` (received ${l.receivedQty})` : ''}</TableCell>
                      <TableCell>{foreign ? (l.foreignUnitPrice ?? '—') : formatMoney(l.unitPrice)}</TableCell>
                      <TableCell>{foreign && po.currencyCode ? formatFc(po.currencyCode, l.foreignLineTotal) : formatMoney(l.lineTotal)}</TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
        </ScrollableTable>
      </Paper>

      {/* Totals */}
      <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 1 }} data-help-section="procurement.poDetail.section.totals">
        {foreign && po.currencyCode ? (
          <>
            <Typography variant="h6">Total: {formatFc(po.currencyCode, po.foreignTotal)}</Typography>
            <Typography variant="body2" color="text.secondary">≈ {formatMoney(po.totalAmount)} at the indicative rate {po.exchangeRate}</Typography>
          </>
        ) : (
          <>
            <Typography>Subtotal: {formatMoney(po.subtotal)}</Typography>
            <Typography>Tax: {formatMoney(po.taxAmount)}</Typography>
            <Typography variant="h6">Total: {formatMoney(po.totalAmount)}</Typography>
          </>
        )}
      </Paper>

      <ConfirmDialog
        open={deleteDialogOpen}
        title="Delete this purchase order?"
        message="This cannot be undone."
        confirmLabel="Delete"
        confirmColor="error"
        onCancel={() => setDeleteDialogOpen(false)}
        onConfirm={handleDelete}
        submitting={deleting}
      />
      {id && <AttachmentsPanel entityType="PURCHASE_ORDER" entityId={id} canManage={canManagePo} />} {/* W3-K */}
    </Box>
  );
};

export default PurchaseOrderDetail;
